import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import Form from '../../components/Form/Form';
import ProgressBarField from '../../components/ProgressBar/ProgressBarField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import DataService from '../../services/data.service';
import { DataResult } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';
import ButtonBar from '../../components/Fields/ButtonBar';
import SaveButton from '../../components/Buttons/SaveButton';
import FileCreateResultModel from '../../models/FileCreateResultModel';
import WorkerProcessStatus from '../../models/WorkerProcessStatus';
import FileUploadField from '../../components/Fields/FileUploadField';
import SelectListField from '../../components/Fields/SelectListField';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';

interface ImportStartModel {
    processName: string
    externalSystemId?: number
    fileName?: string
    file?: File
    fileResult?: FileCreateResultModel
}

const SendMessageView: React.FC = () => {
    const importProcesses = useSelectListOptions('api/v1/selectlist/ImportProcesses', '-- Select process');
    const externalSystems = useSelectListOptions('api/v1/selectlist/ExternalSystems', '-- Select external system');
    const [serverErrors, setServerErrors] = useState<DataResult<ImportStartModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException, notify } = useNotifications();
    const [connection, setConnection] = useState<HubConnection>();
    const [progress, setProgress] = useState<WorkerProcessStatus>({currentItem: 0, message: '', dateTime: '', processId: '', processName: ' ', processType: '', progress: 0, status: 0, totalItems: 0});
    const { user } = useAuth();

    const [formData, setFormData] = useState<ImportStartModel>({
        processName: '',
        externalSystemId: 0,
    });

    useEffect(() => {
        if (user) {
            let url: string | undefined
            switch (user.environment) {
                case 'dev':
                    url = process.env.REACT_APP_API_ENDPOINT_dev;
                    break;
                case 'tst':
                    url = process.env.REACT_APP_API_ENDPOINT_tst;
                    break;
                case 'prd':
                    url = process.env.REACT_APP_API_ENDPOINT_prd;
                    break;
            }

            const newConnection = new HubConnectionBuilder()
                .withUrl(url + 'hubs/importhub', { accessTokenFactory: () => user.token })
                .withAutomaticReconnect()
                .build();

            setConnection(newConnection);
        }
    }, [user?.token]);

    useEffect(() => {
        if (connection) {
            connection.start()
                .then(result => {
                    console.log('Connected to import hub!');

                    connection.on('HandleProgressChanged', p => {
                        setProgress(p)
                        console.log('Progress Changed', p)
                    });

                    connection.on('HandleStatusChanged', p => {
                        setProgress(p);
                        console.log('Status changed:', p)
                    });

                    connection.on('HandleCompleted', p => {
                        setProgress(p)
                        console.log('Completed: ', p)
                    });
                })
                .catch(e => {
                    notify("warning", "Unable to connect to import hub.")
                    console.log('Connection to import hub failed: ', e)
                });
        }
    }, [connection]);

    async function submit() {
        if (formData && formData.file) {
            var fileResponse = await DataService.createFile(formData.file)
            handleResult(fileResponse);
            if (fileResponse.isSuccesful === true && fileResponse.data) {
                formData.fileResult = fileResponse.data;
                DataService
                    .create<ImportStartModel>(`api/v1/Imports`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                    })
                    .catch(e => handleException(e));
            }
            else {
                handleResult(fileResponse);
            }
        }
    }

    function onFieldChanged(name: string, value: string | File): void {
        if (name === 'fileName' && typeof (value) === 'object') {
            //setFile(value);
            formData.file = value;
            formData.fileName = value.name;
        }
        else {
            formData[name] = value;
        }
        setFormData(formData);
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'processName', label: 'Process', validationRules: [{ type: 'isRequired' }] },
            { name: 'externalSystemId', label: 'Import from' },
            { name: 'fileName', label: 'File', validationRules: [{ type: 'isRequired' }] },
        ],
        handleSubmit: submit,
        handleFieldChanged: onFieldChanged,
        showErrors: 'always',
    };


    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedForImportType } = useAuth();
    if (!isAuthorizedForImportType(ApplicationFunctions.StartImportProcess, undefined))
        return (<Unauthorized />);

    return (
        <div className='main-content'>
            <Form title='Send message'>
                <Form.Content >
                    <SelectListField {...getFieldProps('processName')} options={importProcesses} />
                    <SelectListField {...getFieldProps('externalSystemId')} options={externalSystems} />
                    <FileUploadField {...getFieldProps('fileName')} />
                    <ProgressBarField labelProps={{ text: progress.processName, labelSize: 2, required: false }} progress={progress} visible={true} />
                </Form.Content>
                <Form.Footer>
                    <ButtonBar offset={2}>
                        <SaveButton  {...getSubmitButtonProps()} text="Import" />
                    </ButtonBar>
                </Form.Footer>
            </Form>
        </div>
    );
}
export default SendMessageView;